import React, { useState, useEffect } from 'react';
import { 
  X, 
  Upload, 
  Download, 
  FileText, 
  CheckSquare, 
  Square,
  RefreshCw,
  FolderDown,
  Eye,
  Trash2
} from 'lucide-react';
import Alert from '../common/Alert.js';
import fileCabinetService from '../../services/EIReceiptFileCabinetService.js';
import { useAuth } from '../../context/AuthContext.js';

const EIReceiptFileCabinet = ({ eiReceiptId, clientId, onClose }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewingImage, setViewingImage] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const { user } = useAuth();

  const isClient = user?.role === 'Client';
  const canUpload = !isClient;
  const canDelete = !isClient && ['System Admin', 'Admin', 'Documentation Manager'].includes(user?.role);

  useEffect(() => {
    fetchFiles();
  }, [eiReceiptId]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fileCabinetService.getFilesByEIReceiptId(eiReceiptId);
      setFiles(response.data || []);
      setSelectedFiles([]);
    } catch (err) {
      console.error('Error fetching files:', err);
      Alert.error(err.response?.data?.error || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const uploadFiles = Array.from(e.target.files);
    
    if (uploadFiles.length === 0) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = uploadFiles.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      Alert.error(`Only image files (JPEG, PNG, GIF, WEBP) are allowed. ${invalidFiles.length} invalid file(s) detected.`);
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = uploadFiles.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      Alert.error(`Some files exceed the 10MB size limit. Please compress them first.`);
      return;
    }

    try {
      setUploading(true);
      const response = await fileCabinetService.uploadFiles(eiReceiptId, clientId, uploadFiles);
      
      if (response.errors && response.errors.length > 0) {
        Alert.warning(`Uploaded ${response.total_uploaded} file(s) with ${response.total_errors} error(s)`);
        console.error('Upload errors:', response.errors);
      } else {
        Alert.success(`Successfully uploaded ${uploadFiles.length} file(s)`);
      }
      
      fetchFiles();
      e.target.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      Alert.error(err.response?.data?.error || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleViewImage = async (fileId) => {
    try {
      const blob = await fileCabinetService.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      setViewingImage(url);
    } catch (err) {
      console.error('View error:', err);
      Alert.error('Failed to load image');
    }
  };

  const closeImageViewer = () => {
    if (viewingImage) {
      window.URL.revokeObjectURL(viewingImage);
      setViewingImage(null);
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const blob = await fileCabinetService.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('File downloaded successfully');
    } catch (err) {
      console.error('Download error:', err);
      Alert.error(err.response?.data?.error || 'Failed to download file');
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedFiles.length === 0) {
      Alert.error('Please select files to download');
      return;
    }

    try {
      if (selectedFiles.length === 1) {
        const file = files.find(f => f.id === selectedFiles[0]);
        await handleDownloadFile(file.id, file.image_name);
      } else {
        const blob = await fileCabinetService.downloadMultipleFiles(selectedFiles);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        link.download = `${eiReceiptId.replace(/\//g, '_')}_selected_${timestamp}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        Alert.success(`Successfully downloaded ${selectedFiles.length} file(s) as ZIP`);
      }
    } catch (err) {
      console.error('Download selected error:', err);
      Alert.error(err.response?.data?.error || 'Failed to download files');
    }
  };

  const handleDownloadAll = async () => {
    if (files.length === 0) {
      Alert.error('No files available to download');
      return;
    }

    try {
      setDownloadingAll(true);
      const blob = await fileCabinetService.downloadByEIReceipt(eiReceiptId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `${eiReceiptId.replace(/\//g, '_')}_all_files_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success(`Successfully downloaded all ${files.length} file(s) as ZIP`);
    } catch (err) {
      console.error('Download all error:', err);
      Alert.error(err.response?.data?.error || 'Failed to download all files');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${fileName}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await fileCabinetService.deleteFile(fileId);
      Alert.success('File deleted successfully');
      setSelectedFiles(prev => prev.filter(id => id !== fileId));
      fetchFiles();
    } catch (err) {
      console.error('Delete file error:', err);
      Alert.error(err.response?.data?.error || 'Failed to delete file');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.length === 0) {
      Alert.error('Please select files to delete');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedFiles.length} selected file(s)? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setDeletingSelected(true);
      await fileCabinetService.deleteMultipleFiles(selectedFiles);
      Alert.success(`Successfully deleted ${selectedFiles.length} file(s)`);
      setSelectedFiles([]);
      fetchFiles();
    } catch (err) {
      console.error('Delete selected error:', err);
      Alert.error(err.response?.data?.error || 'Failed to delete files');
    } finally {
      setDeletingSelected(false);
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => {
      if (prev.includes(fileId)) {
        return prev.filter(id => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(file => file.id));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getTotalSize = () => {
    return files.reduce((sum, file) => sum + (file.file_size || 0), 0);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gradient-to-r from-green-600 to-green-700">
            <div className="text-white min-w-0">
              <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <FolderDown className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                <span className="truncate">EI Receipt File Cabinet</span>
              </h2>
              <p className="text-xs sm:text-sm text-green-100 mt-1 truncate">EI Receipt: {eiReceiptId}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-green-800 rounded-full transition-colors text-white flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Action Bar */}
          <div className="p-2 sm:p-4 border-b bg-gray-50 flex flex-wrap gap-2 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {files.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-xs sm:text-sm font-medium"
                  title={selectedFiles.length === files.length ? 'Deselect All' : 'Select All'}
                >
                  {selectedFiles.length === files.length ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">
                    {selectedFiles.length === files.length ? 'Deselect All' : 'Select All'}
                  </span>
                </button>
              )}

              {canUpload && (
                <label className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer transition-colors text-xs sm:text-sm font-medium">
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">{uploading ? 'Uploading...' : 'Upload'}</span>
                  <span className="sm:hidden">{uploading ? '...' : '⬆'}</span>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
              
              <button
                onClick={fetchFiles}
                disabled={loading}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400 transition-colors text-xs sm:text-sm font-medium"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              
              {files.length > 0 && (
                <>
                  <button
                    onClick={handleDownloadSelected}
                    disabled={selectedFiles.length === 0}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-xs sm:text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Download</span>
                    <span className="text-xs">({selectedFiles.length})</span>
                  </button>

                  <button
                    onClick={handleDownloadAll}
                    disabled={downloadingAll}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-xs sm:text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">{downloadingAll ? 'Downloading...' : 'All'}</span>
                    <span className="sm:hidden">{downloadingAll ? '...' : '⬇'}</span>
                    <span className="text-xs">({files.length})</span>
                  </button>

                  {canDelete && (
                    <button
                      onClick={handleDeleteSelected}
                      disabled={selectedFiles.length === 0 || deletingSelected}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 transition-colors text-xs sm:text-sm font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="hidden sm:inline">{deletingSelected ? 'Deleting...' : 'Delete'}</span>
                      <span className="text-xs">({selectedFiles.length})</span>
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="text-xs sm:text-sm text-gray-600 font-medium">
              {files.length} files {selectedFiles.length > 0 && `• ${selectedFiles.length} selected`}
            </div>
          </div>

          {/* File Grid */}
          <div className="flex-1 overflow-auto p-2 sm:p-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
                  <p className="text-gray-600">Loading files...</p>
                </div>
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FileText className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg font-medium">No files uploaded yet</p>
                <p className="text-sm mt-2">Upload image files to get started</p>
                {canUpload && (
                  <label className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer transition-colors text-sm font-medium">
                    Upload Files
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                      selectedFiles.includes(file.id)
                        ? 'border-green-500 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Image Container */}
                    <div className="relative w-full h-32 sm:h-40 bg-gray-100 overflow-hidden">
                      {file.image_url ? (
                        <img
                          src={file.image_url}
                          alt={file.image_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.querySelector('.fallback-placeholder')?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      
                      <div className={`w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center fallback-placeholder ${file.image_url ? 'hidden' : ''}`}>
                        <FileText className="w-8 h-8 text-gray-400" />
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFileSelection(file.id);
                        }}
                        className="absolute top-2 left-2 p-1 bg-white/90 hover:bg-white rounded transition-colors z-10 flex-shrink-0"
                        title="Select image"
                      >
                        {selectedFiles.includes(file.id) ? (
                          <CheckSquare className="w-5 h-5 text-green-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      <div className="absolute top-2 right-2 flex gap-1 z-20">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewImage(file.id);
                          }}
                          className="p-1.5 sm:p-2 bg-white/90 hover:bg-white rounded transition-colors flex-shrink-0"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(file.id, file.image_name);
                          }}
                          className="p-1.5 sm:p-2 bg-white/90 hover:bg-white rounded transition-colors flex-shrink-0"
                          title="Download"
                        >
                          <Download className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        </button>
                        {canDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFile(file.id, file.image_name);
                            }}
                            className="p-1.5 sm:p-2 bg-white/90 hover:bg-white rounded transition-colors flex-shrink-0"
                            title="Delete File"
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="p-2 bg-white">
                      <p className="text-xs truncate font-medium text-gray-900" title={file.image_name}>
                        {file.image_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 sm:p-4 border-t bg-gray-50 flex justify-between items-center">
            <div className="text-xs sm:text-sm text-gray-600">
              {files.length > 0 && (
                <span>
                  {files.length} file{files.length !== 1 ? 's' : ''} 
                  {selectedFiles.length > 0 && ` • ${selectedFiles.length} selected`}
                  {' • '}{formatFileSize(getTotalSize())}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors font-medium text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Image Viewer Modal */}
      {viewingImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={closeImageViewer}
        >
          <button
            onClick={closeImageViewer}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-200 transition-colors z-[70]"
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={viewingImage} 
            alt="Preview" 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default EIReceiptFileCabinet;